import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  TextField,
  InputAdornment,
  Divider,
  Chip,
  Stack,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  PlayArrow as StartIcon,
  Stop as EndIcon,
  Api as APIIcon,
  Storage as DatabaseIcon,
  Email as EmailIcon,
  Approval as ApprovalIcon,
  CallSplit as DecisionIcon,
  AccountTree as ParallelIcon,
  MergeType as MergeIcon,
  Loop as LoopIcon,
  Timer as TimerIcon,
  Error as ErrorIcon,
  SubdirectoryArrowRight as SubprocessIcon,
  Note as NoteIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useReactFlow } from 'reactflow';

const NodePalette = ({ onAddNode, disabled = false }) => {
  const theme = useTheme();
  const { project, getViewport } = useReactFlow();
  const [isExpanded, setIsExpanded] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    flow: true,
    decision: true,
    integration: true,
    utility: true
  });

  // Node categories and their configurations - using theme colors
  const nodeCategories = useMemo(() => [
    {
      id: 'flow',
      name: 'Flow Control',
      icon: <CategoryIcon />,
      color: theme.palette.success.main,
      nodes: [
        {
          type: 'start',
          label: 'Start',
          icon: <StartIcon />,
          description: 'Entry point of the process',
          color: theme.palette.success.main
        },
        {
          type: 'end',
          label: 'End',
          icon: <EndIcon />,
          description: 'Exit point of the process',
          color: theme.palette.error.main
        }
      ]
    },
    {
      id: 'decision',
      name: 'Decision & Routing',
      icon: <DecisionIcon />,
      color: theme.palette.warning.main,
      nodes: [
        {
          type: 'decision',
          label: 'Decision',
          icon: <DecisionIcon />,
          description: 'Conditional branching logic',
          color: theme.palette.warning.main
        },
        {
          type: 'parallelGateway',
          label: 'Parallel Split',
          icon: <ParallelIcon />,
          description: 'Split flow into parallel paths',
          color: theme.palette.secondary.main
        },
        {
          type: 'mergeGateway',
          label: 'Merge',
          icon: <MergeIcon />,
          description: 'Merge parallel paths',
          color: theme.palette.secondary.main
        },
        {
          type: 'loop',
          label: 'Loop',
          icon: <LoopIcon />,
          description: 'Iterative processing',
          color: theme.palette.primary.main
        }
      ]
    },
    {
      id: 'integration',
      name: 'Integration & Data',
      icon: <APIIcon />,
      color: theme.palette.info.main,
      nodes: [
        {
          type: 'api',
          label: 'API Call',
          icon: <APIIcon />,
          description: 'External API integration',
          color: theme.palette.info.main
        },
        {
          type: 'database',
          label: 'Database',
          icon: <DatabaseIcon />,
          description: 'Database operations',
          color: theme.palette.secondary.dark
        },
        {
          type: 'email',
          label: 'Email',
          icon: <EmailIcon />,
          description: 'Send email notifications',
          color: theme.palette.error.light
        },
        {
          type: 'approval',
          label: 'Approval',
          icon: <ApprovalIcon />,
          description: 'Manual approval step',
          color: theme.palette.primary.dark
        }
      ]
    },
    {
      id: 'utility',
      name: 'Utilities',
      icon: <CategoryIcon />,
      color: theme.palette.grey[600],
      nodes: [
        {
          type: 'timer',
          label: 'Timer',
          icon: <TimerIcon />,
          description: 'Delay or scheduled execution',
          color: theme.palette.warning.dark
        },
        {
          type: 'errorHandler',
          label: 'Error Handler',
          icon: <ErrorIcon />,
          description: 'Exception handling',
          color: theme.palette.error.main
        },
        {
          type: 'subprocess',
          label: 'Subprocess',
          icon: <SubprocessIcon />,
          description: 'Call another process',
          color: theme.palette.info.dark
        },
        {
          type: 'note',
          label: 'Note',
          icon: <NoteIcon />,
          description: 'Documentation annotation',
          color: theme.palette.warning.light
        }
      ]
    }
  ], [theme]);

  // Filter nodes based on search term
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return nodeCategories;

    return nodeCategories.map(category => ({
      ...category,
      nodes: category.nodes.filter(node =>
        node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.type.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(category => category.nodes.length > 0);
  }, [searchTerm, nodeCategories]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleNodeDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleNodeClick = (nodeType) => {
    if (disabled || !onAddNode) return;

    // Calculate center position for new node
    const viewport = getViewport();
    const position = project({
      x: (window.innerWidth - (isExpanded ? 280 : 60)) / 2,
      y: window.innerHeight / 2
    });

    const nodeConfig = nodeCategories
      .flatMap(cat => cat.nodes)
      .find(node => node.type === nodeType);

    if (nodeConfig) {
      onAddNode(nodeType, position, nodeConfig);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const paletteWidth = isExpanded ? 280 : 60;
  const isDarkMode = theme.palette.mode === 'dark';

  // Theme-based colors
  const paletteBackground = isDarkMode
    ? alpha(theme.palette.background.paper, 0.95)
    : theme.palette.background.paper;
  const borderColor = isDarkMode ? theme.palette.secondary.main : theme.palette.divider;
  const headerBackground = isDarkMode
    ? alpha(theme.palette.secondary.main, 0.1)
    : alpha(theme.palette.primary.main, 0.05);

  return (
    <Paper
      sx={{
        position: 'absolute',
        top: 16,
        left: 16,
        width: paletteWidth,
        height: 'calc(100vh - 120px)',
        zIndex: 1000,
        backgroundColor: paletteBackground,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(borderColor, 0.2)}`,
        transition: 'width 0.3s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1,
          borderBottom: `1px solid ${alpha(borderColor, 0.2)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: headerBackground
        }}
      >
        {isExpanded && (
          <Typography
            variant="h6"
            sx={{
              color: isDarkMode ? theme.palette.secondary.main : theme.palette.primary.main,
              fontWeight: 'bold',
              fontSize: '1rem'
            }}
          >
            Node Palette
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            color: isDarkMode ? theme.palette.secondary.main : theme.palette.primary.main,
            '&:hover': {
              backgroundColor: isDarkMode
                ? alpha(theme.palette.secondary.main, 0.1)
                : alpha(theme.palette.primary.main, 0.1)
            }
          }}
        >
          {isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>

      {/* Search Field */}
      {isExpanded && (
        <Box sx={{ p: 1, borderBottom: `1px solid ${alpha(borderColor, 0.2)}` }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search nodes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: alpha(theme.palette.text.secondary, 0.7) }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon sx={{ color: alpha(theme.palette.text.secondary, 0.7) }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              backgroundColor: isDarkMode
                ? alpha(theme.palette.background.default, 0.3)
                : alpha(theme.palette.grey[100], 0.5),
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: alpha(borderColor, 0.3)
                },
                '&:hover fieldset': {
                  borderColor: alpha(borderColor, 0.5)
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? borderColor : theme.palette.primary.main
                },
                '& input': {
                  color: theme.palette.text.primary,
                  '&::placeholder': {
                    color: alpha(theme.palette.text.secondary, 0.7)
                  }
                }
              }
            }}
          />
        </Box>
      )}

      {/* Node Categories */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {isExpanded ? (
          <Box sx={{ p: 1 }}>
            {filteredCategories.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  color: alpha(theme.palette.text.secondary, 0.7),
                  p: 2
                }}
              >
                No nodes found
              </Typography>
            ) : (
              filteredCategories.map(category => (
                <Box key={category.id} sx={{ mb: 1 }}>
                  {/* Category Header */}
                  <Box
                    onClick={() => toggleCategory(category.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1,
                      cursor: 'pointer',
                      backgroundColor: alpha(category.color, 0.1),
                      borderBottom: `1px solid ${alpha(borderColor, 0.1)}`,
                      '&:hover': {
                        backgroundColor: alpha(category.color, 0.2)
                      }
                    }}
                  >
                    <Box sx={{ color: category.color }}>{category.icon}</Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        ml: 1,
                        color: theme.palette.text.primary,
                        fontWeight: 'bold',
                        flexGrow: 1
                      }}
                    >
                      {category.name}
                    </Typography>
                    <Chip
                      label={category.nodes.length}
                      size="small"
                      sx={{
                        minWidth: 24,
                        height: 20,
                        backgroundColor: alpha(category.color, 0.2),
                        color: theme.palette.text.primary,
                        fontSize: '0.75rem'
                      }}
                    />
                  </Box>

                  {/* Category Nodes */}
                  <Collapse in={expandedCategories[category.id]}>
                    <Box sx={{ pl: 1, pr: 1, pt: 0.5 }}>
                      {category.nodes.map(node => (
                        <Paper
                          key={node.type}
                          elevation={1}
                          draggable={!disabled}
                          onDragStart={(e) => handleNodeDragStart(e, node.type)}
                          onClick={() => handleNodeClick(node.type)}
                          sx={{
                            mb: 0.5,
                            p: 1,
                            cursor: disabled ? 'not-allowed' : 'grab',
                            opacity: disabled ? 0.5 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: isDarkMode
                              ? alpha(theme.palette.background.default, 0.3)
                              : theme.palette.grey[50],
                            border: `1px solid ${isDarkMode ? 'transparent' : theme.palette.divider}`,
                            '&:hover': {
                              backgroundColor: disabled ? 'transparent' : alpha(node.color, isDarkMode ? 0.1 : 0.05),
                              borderColor: disabled ? theme.palette.divider : alpha(node.color, 0.3),
                              transform: disabled ? 'none' : 'translateX(4px)',
                              transition: 'all 0.2s ease'
                            },
                            '&:active': {
                              cursor: disabled ? 'not-allowed' : 'grabbing'
                            }
                          }}
                        >
                          <Box sx={{ color: node.color, fontSize: '1.2rem' }}>
                            {node.icon}
                          </Box>
                          <Box sx={{ ml: 1.5, flexGrow: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 500,
                                fontSize: '0.875rem'
                              }}
                            >
                              {node.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: alpha(theme.palette.text.secondary, 0.7),
                                fontSize: '0.75rem',
                                display: 'block'
                              }}
                            >
                              {node.description}
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              ))
            )}
          </Box>
        ) : (
          // Collapsed view - icon only
          <Stack spacing={1} sx={{ p: 1 }}>
            {filteredCategories.map(category => (
              <Tooltip
                key={category.id}
                title={category.name}
                placement="right"
              >
                <IconButton
                  size="small"
                  sx={{ color: alpha(theme.palette.text.secondary, 0.7) }}
                >
                  {category.icon}
                </IconButton>
              </Tooltip>
            ))}
            <Divider />
            {filteredCategories.flatMap(cat => cat.nodes.slice(0, 3)).map(node => (
              <Tooltip
                key={node.type}
                title={node.label}
                placement="right"
              >
                <IconButton
                  size="small"
                  draggable={!disabled}
                  onDragStart={(e) => handleNodeDragStart(e, node.type)}
                  onClick={() => handleNodeClick(node.type)}
                  disabled={disabled}
                  sx={{
                    color: node.color,
                    backgroundColor: alpha(node.color, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(node.color, 0.2)
                    }
                  }}
                >
                  {node.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Stack>
        )}
      </Box>

      {/* Footer */}
      {isExpanded && (
        <Box
          sx={{
            p: 1,
            borderTop: `1px solid ${alpha(borderColor, 0.2)}`,
            backgroundColor: alpha(theme.palette.secondary.main, 0.05)
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: alpha(theme.palette.text.secondary, 0.7),
              display: 'block',
              textAlign: 'center'
            }}
          >
            Drag or click to add
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default NodePalette;